'use client';

import { useState, useEffect } from 'react';
import { CheckCircle2, X } from 'lucide-react';
import Image from 'next/image';

interface Endorser {
  id: string;
  company_name: string;
  logo_url: string | null;
}

interface EndorsementUIProps {
  forwarderId: string;
  initialEndorsements?: Endorser[];
  currentUserId?: string | null;
  isCurrentUserForwarder?: boolean;
}

/**
 * Get company initials for logo fallback
 */
function getCompanyInitials(companyName: string): string {
  return companyName
    .split(' ')
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

export function EndorsementUI({
  forwarderId,
  initialEndorsements = [],
  currentUserId: currentUserIdProp,
  isCurrentUserForwarder: isCurrentUserForwarderProp,
}: EndorsementUIProps) {
  const [endorsers, setEndorsers] = useState<Endorser[]>(initialEndorsements);
  const [hasEndorsed, setHasEndorsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchedUserId, setFetchedUserId] = useState<string | null>(null);
  const [fetchedIsForwarder, setFetchedIsForwarder] = useState(false);

  // Self-fetch user state only when the parent doesn't pass it (ISR-rendered pages).
  const shouldSelfFetch = currentUserIdProp === undefined && isCurrentUserForwarderProp === undefined;

  useEffect(() => {
    if (!shouldSelfFetch) return;
    let cancelled = false;
    fetch('/api/auth/me')
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const u = data?.user ?? null;
        setFetchedUserId(u?.id ?? null);
        setFetchedIsForwarder(u?.role === 'forwarder');
      })
      .catch(() => {
        // Anonymous viewer — leave defaults.
      });
    return () => {
      cancelled = true;
    };
  }, [shouldSelfFetch]);

  const currentUserId = shouldSelfFetch ? fetchedUserId : (currentUserIdProp ?? null);
  const isCurrentUserForwarder = shouldSelfFetch ? fetchedIsForwarder : !!isCurrentUserForwarderProp;

  useEffect(() => {
    if (currentUserId) {
      setHasEndorsed(endorsers.some((e) => e.id === currentUserId));
    }
  }, [currentUserId, endorsers]);

  const canEndorse = isCurrentUserForwarder && currentUserId !== forwarderId;

  const handleEndorse = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      if (hasEndorsed) {
        // Remove endorsement
        await fetch('/api/endorsements', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endorsed_id: forwarderId }),
        });
        setEndorsers((prev) => prev.filter((e) => e.id !== currentUserId));
      } else {
        // Add endorsement
        await fetch('/api/endorsements', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endorsed_id: forwarderId }),
        });
        // Refetch endorsements to get complete data
        const res = await fetch(`/api/endorsements?endorsed_id=${forwarderId}`);
        const data = await res.json();
        if (data.endorsements) {
          setEndorsers(
            data.endorsements.map((e: { endorser: Endorser }) => e.endorser)
          );
        }
      }
    } catch (err) {
      console.error('Endorsement error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="endorsement-container" suppressHydrationWarning>
      {/* Avatar stack with endorsement text */}
      {endorsers.length > 0 && (
        <div className="endorsement-display">
          <div className="endorsers-avatars">
            {endorsers.slice(0, 5).map((endorser, i) => (
              <div
                key={endorser.id}
                className="endorser-avatar-box"
                style={{ zIndex: endorsers.length - i }}
                title={endorser.company_name}
              >
                {endorser.logo_url ? (
                  <Image
                    src={endorser.logo_url}
                    alt={endorser.company_name}
                    width={32}
                    height={32}
                    className="endorser-logo-img"
                  />
                ) : (
                  <span className="endorser-initials">
                    {getCompanyInitials(endorser.company_name)}
                  </span>
                )}
              </div>
            ))}
            {endorsers.length > 5 && (
              <div className="endorser-avatar-box endorser-more-count" style={{ zIndex: 0 }}>
                <span className="more-count-text">
                  +{endorsers.length - 5}
                </span>
              </div>
            )}
          </div>
          <span className="endorsement-label">
            <CheckCircle2 className="endorsement-icon" />
            {endorsers.length} peer endorsement{endorsers.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Endorse button - only visible to forwarders */}
      {canEndorse && (
        <button
          onClick={handleEndorse}
          disabled={isLoading}
          className={`endorsement-button ${hasEndorsed ? 'endorsed' : ''} ${isLoading ? 'loading' : ''}`}
        >
          {hasEndorsed ? (
            <>
              <X className="button-icon" />
              <span>ENDORSED</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="button-icon" />
              <span>ENDORSE</span>
            </>
          )}
        </button>
      )}

      <style jsx>{`
        .endorsement-container {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .endorsement-display {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .endorsers-avatars {
          display: flex;
          margin-left: -4px;
        }

        .endorser-avatar-box {
          position: relative;
          width: 32px;
          height: 32px;
          border: 2px solid #000;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: -8px;
          transition: transform 0.2s;
        }

        .endorser-avatar-box:hover {
          transform: translateY(-2px);
        }

        .endorser-logo-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .endorser-initials {
          font-size: 11px;
          font-weight: 700;
          color: #000;
          text-transform: uppercase;
        }

        .endorser-more-count {
          background: #f5f5f5;
        }

        .more-count-text {
          font-size: 11px;
          font-weight: 700;
          color: #000;
        }

        .endorsement-label {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          font-weight: 500;
          color: #333;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .endorsement-icon {
          width: 14px;
          height: 14px;
          color: #10b981;
        }

        .endorsement-button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: 2px solid #000;
          background: #fff;
          color: #000;
          font-size: 13px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .endorsement-button:hover:not(:disabled) {
          background: #000;
          color: #fff;
        }

        .endorsement-button.endorsed {
          background: #10b981;
          border-color: #10b981;
          color: #fff;
        }

        .endorsement-button.endorsed:hover:not(:disabled) {
          background: #059669;
          border-color: #059669;
        }

        .endorsement-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .button-icon {
          width: 16px;
          height: 16px;
        }

        @media (max-width: 640px) {
          .endorsement-container {
            flex-direction: column;
            align-items: flex-start;
          }

          .endorsement-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
